import { tokens } from './constants.js';

/**
 * Parse a CSS gradient string into an AST
 */
class GradientParserCore {
  constructor() {
    this.input = '';
  }

  /**
   * Parse gradient string
   * @param {string} code - CSS gradient string
   * @returns {Array} Array of gradient AST nodes
   */
  parse(code) {
    this.input = this.fixUnbalancedParentheses(code.toString());
    return this.getAST();
  }

  /**
   * Fix unbalanced parentheses in gradient strings
   */
  fixUnbalancedParentheses(str) {
    let openCount = 0;
    let closeCount = 0;

    for (let i = 0; i < str.length; i++) {
      if (str[i] === '(') openCount++;
      else if (str[i] === ')') closeCount++;
    }

    if (openCount > closeCount) {
      str += ')'.repeat(openCount - closeCount);
    }

    return str;
  }

  error(msg) {
    const err = new Error(this.input + ': ' + msg);
    err.source = this.input;
    throw err;
  }

  getAST() {
    const ast = this.matchListDefinitions();

    if (this.input.length > 0) {
      this.error('Invalid input not EOF');
    }

    return ast;
  }

  matchListDefinitions() {
    return this.matchListing(() => this.matchDefinition());
  }

  matchDefinition() {
    return (
      this.matchGradient('linear-gradient', tokens.linearGradient, () =>
        this.matchLinearOrientation()
      ) ||
      this.matchGradient('repeating-linear-gradient', tokens.repeatingLinearGradient, () =>
        this.matchLinearOrientation()
      ) ||
      this.matchGradient('radial-gradient', tokens.radialGradient, () =>
        this.matchListRadialOrientations()
      ) ||
      this.matchGradient('repeating-radial-gradient', tokens.repeatingRadialGradient, () =>
        this.matchListRadialOrientations()
      ) ||
      this.matchGradient('conic-gradient', tokens.conicGradient, () => this.matchConicOrientation())
    );
  }

  matchGradient(gradientType, pattern, orientationMatcher) {
    return this.matchCall(pattern, () => {
      const orientation = orientationMatcher();
      if (orientation) {
        if (!this.scan(tokens.comma)) {
          this.error('Missing comma before color stops');
        }
      }

      const colorStops = this.matchListing(() => this.matchColorStop());
      this.addDefaultLengthsToColorStops(colorStops);

      return {
        type: gradientType,
        orientation,
        colorStops,
      };
    });
  }

  addDefaultLengthsToColorStops(colorStops) {
    if (!colorStops || colorStops.length === 0) return;

    const needsDefaultLengths = colorStops.some((stop) => !stop.length);

    if (needsDefaultLengths) {
      for (let i = 0; i < colorStops.length; i++) {
        if (!colorStops[i].length) {
          let percentage;
          if (colorStops.length === 1) {
            percentage = '50';
          } else {
            percentage = ((i / (colorStops.length - 1)) * 100).toString();
          }

          colorStops[i].length = {
            type: '%',
            value: percentage,
          };
        }
      }
    }
  }

  matchCall(pattern, callback) {
    const captures = this.scan(pattern);

    if (captures) {
      if (!this.scan(tokens.startCall)) {
        this.error('Missing (');
      }

      const result = callback(captures);

      if (!this.scan(tokens.endCall)) {
        this.error('Missing )');
      }

      return result;
    }
  }

  matchConicOrientation() {
    let angle;
    let position = null;

    if (this.scan(tokens.from)) {
      angle = this.matchAngle();
      if (!angle) {
        this.error('Expected angle after "from"');
      }
    } else {
      angle = this.matchAngle();
    }

    if (this.scan(tokens.at)) {
      position = this.matchCoordinates();
      if (!position.x && !position.y) {
        this.error('Expected coordinates after "at"');
      }
    }

    if (angle || position) {
      return {
        type: 'conic',
        value: angle ? angle.value : 0,
        position,
      };
    }
    return null;
  }

  matchLinearOrientation() {
    return this.matchSideOrCorner() || this.matchAngle();
  }

  matchSideOrCorner() {
    return this.match('directional', tokens.sideOrCorner, 1);
  }

  matchAngle() {
    return this.match('angular', tokens.angleValue, 1);
  }

  matchListRadialOrientations() {
    let radialOrientation = this.matchRadialOrientation();
    let radialOrientations;

    if (radialOrientation) {
      radialOrientations = [radialOrientation];
      const lookaheadCache = this.input;

      if (this.scan(tokens.comma)) {
        radialOrientation = this.matchRadialOrientation();
        if (radialOrientation) {
          radialOrientations.push(radialOrientation);
        } else {
          this.input = lookaheadCache;
        }
      }
    }

    return radialOrientations;
  }

  matchRadialOrientation() {
    let radialType = this.matchCircle() || this.matchEllipse();

    if (radialType) {
      radialType.at = this.matchAtPosition();
    } else {
      const extent = this.matchExtentKeyword();
      if (extent) {
        radialType = extent;
        const positionAt = this.matchAtPosition();
        if (positionAt) {
          radialType.at = positionAt;
        }
      } else {
        const defaultPosition = this.matchPositioning();
        if (defaultPosition) {
          radialType = {
            type: 'default-radial',
            at: defaultPosition,
          };
        }
      }
    }

    return radialType;
  }

  matchCircle() {
    const circle = this.match('shape', /^(circle)/i, 0);
    if (circle) {
      circle.style = this.matchLength() || this.matchExtentKeyword();
    }
    return circle;
  }

  matchEllipse() {
    const ellipse = this.match('shape', /^(ellipse)/i, 0);
    if (ellipse) {
      ellipse.style = this.matchDistance() || this.matchExtentKeyword();
    }
    return ellipse;
  }

  matchExtentKeyword() {
    return this.match('extent-keyword', tokens.extentKeywords, 1);
  }

  matchAtPosition() {
    if (this.match('position', /^at/, 0)) {
      const positioning = this.matchPositioning();
      if (!positioning) {
        this.error('Missing positioning value');
      }
      return positioning;
    }
  }

  matchPositioning() {
    const location = this.matchCoordinates();
    if (location.x || location.y) {
      return {
        type: 'position',
        value: location,
      };
    }
  }

  matchCoordinates() {
    return {
      x: this.matchDistance(),
      y: this.matchDistance(),
    };
  }

  matchListing(matcher) {
    let captures = matcher();
    const result = [];

    if (captures) {
      result.push(captures);
      while (this.scan(tokens.comma)) {
        captures = matcher();
        if (captures) {
          result.push(captures);
        } else {
          this.error('One extra comma');
        }
      }
    }

    return result;
  }

  matchColorStop() {
    const color = this.matchColor();
    if (!color) {
      this.error('Expected color definition');
    }
    color.length = this.matchDistance();
    return color;
  }

  matchColor() {
    return (
      this.matchHexColor() ||
      this.matchRGBAColor() ||
      this.matchRGBColor() ||
      this.matchLiteralColor()
    );
  }

  matchLiteralColor() {
    return this.match('literal', tokens.literalColor, 0);
  }

  matchHexColor() {
    return this.match('hex', tokens.hexColor, 1);
  }

  matchRGBColor() {
    return this.matchCall(tokens.rgbColor, () => ({
      type: 'rgb',
      value: this.matchListing(() => this.matchNumber()),
    }));
  }

  matchRGBAColor() {
    return this.matchCall(tokens.rgbaColor, () => ({
      type: 'rgba',
      value: this.matchListing(() => this.matchNumber()),
    }));
  }

  matchNumber() {
    return this.scan(tokens.number)[1];
  }

  matchDistance() {
    return (
      this.match('%', tokens.percentageValue, 1) ||
      this.matchPositionKeyword() ||
      this.matchLength() ||
      this.matchZero()
    );
  }

  matchPositionKeyword() {
    return this.match('position-keyword', tokens.positionKeywords, 1);
  }

  matchLength() {
    return this.match('px', tokens.pixelValue, 1) || this.match('em', tokens.emValue, 1);
  }

  matchZero() {
    const captures = this.scan(tokens.zeroValue);
    if (captures) {
      return {
        type: '%',
        value: '0',
      };
    }
  }

  match(type, pattern, captureIndex) {
    const captures = this.scan(pattern);
    if (captures) {
      return {
        type,
        value: captures[captureIndex],
      };
    }
  }

  scan(regexp) {
    // Skip whitespace
    const blankCaptures = /^[\n\r\t\s]+/.exec(this.input);
    if (blankCaptures) {
      this.consume(blankCaptures[0].length);
    }

    const captures = regexp.exec(this.input);
    if (captures) {
      this.consume(captures[0].length);
    }

    return captures;
  }

  consume(size) {
    this.input = this.input.substr(size);
  }
}

// Create singleton instance for backward compatibility
const parserInstance = new GradientParserCore();

/**
 * Parse a CSS gradient string
 * @param {string} code - CSS gradient string
 * @returns {Array} Array of gradient AST nodes
 */
export function parse(code) {
  return parserInstance.parse(code);
}

export { GradientParserCore };
